import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import { FONT_TOOLBAR_ENTRY } from "../../utils/quillFonts";
import ImageResize from "quill-image-resize-module-react";
import QuillTableBetter from "quill-table-better";
import FinalizeImportedWorkspaceDocument from "./FinalizeImportedWorkspaceDocument";
import { parseHTMLFile } from "../documentation/htmlParsingUtils";

// Task Reference
import "../shared/TaskReferenceBlot";
import TaskReferenceDropdown from "../shared/TaskReferenceDropdown";
import { useTaskReference } from "../../utils/useTaskReference";
import "../shared/UserMentionBlot";
import UserMentionDropdown from "../shared/UserMentionDropdown";

// Styles imports
import "quill-better-table/dist/quill-better-table.css";
import "react-quill-new/dist/quill.snow.css";
import "react-quill/dist/quill.snow.css";
import "../../css/ImportDocument.css";
import "../../css/UploadDocument.css";
import "../../css/ViewAndEditUploadedFile.css";
import "../../css/QuillOverrides.css";
import "../../css/TaskReference.css";
import "../../css/DocEditorMakeover.css";

Quill.register({ "modules/table-better": QuillTableBetter }, true);
Quill.register("modules/imageResize", ImageResize);

// Quill Core Imports
const Inline = Quill.import("blots/inline");
const Block = Quill.import("blots/block");

// Custom Inline Code Block
class InlineCode extends Inline {
    static create() {
        const node = super.create();
        node.setAttribute("class", "inline-code");
        return node;
    }

    static formats(node) {
        return true;
    }
}

InlineCode.blotName = "inline-code";
InlineCode.tagName = "code";

// Custom Multi-line Blockquote
class MultiLineBlockquote extends Block {
    static create() {
        const node = super.create();
        node.setAttribute("class", "multi-line-blockquote");
        return node;
    }
    static formats(node) {
        return true;
    }
}

MultiLineBlockquote.blotName = "multi-line-blockquote";
MultiLineBlockquote.tagName = "multi-line-blockquote";
MultiLineBlockquote.className = "multi-line-blockquote";

// Register Custom Quill Components
Quill.register(MultiLineBlockquote);
Quill.register(InlineCode);

function ViewAndEditUploadedWorkspaceFile({ onBack, documentTitle, selectedFile, imageAttachments = [], workspaceId, enhancedRightClickEnabled, showContextMenu, ContextMenuComponent }) {
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [content, setContent] = useState("");
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showFinalize, setShowFinalize] = useState(false);
    const quillRef = useRef(null);
    const { taskRefProps, userMentionProps } = useTaskReference(quillRef, { mode: "workspace", workspaceId });
    const hasProcessedFile = useRef(false); // Add this ref to track if file has been processed

    // Parse HTML file content when component mounts
    useEffect(() => {
        if (selectedFile && !hasProcessedFile.current) {
            hasProcessedFile.current = true; // Mark as processed
            parseHTMLFileAndSetContent();
        }
    }, [selectedFile, imageAttachments]);

    // Reset the processed flag when selectedFile changes
    useEffect(() => {
        hasProcessedFile.current = false;
    }, [selectedFile]);

    const parseHTMLFileAndSetContent = async () => {
        try {
            const parsedContent = await parseHTMLFile(selectedFile, imageAttachments);
            
            // Debug: Log if we have any tables in the final content
            const finalDoc = new DOMParser().parseFromString(parsedContent, 'text/html');
            const finalTables = finalDoc.querySelectorAll('table');
            
            // If we have tables, use the alternative approach directly
            if (finalTables.length > 0) {
                setTimeout(() => {
                    if (quillRef.current) {
                        const editor = quillRef.current.getEditor();
                        
                        try {
                            // Clear existing content first
                            editor.setContents([]);
                            
                            // Use the clipboard method which is more reliable for tables
                            editor.clipboard.dangerouslyPasteHTML(0, parsedContent);
                            
                            // Check result after another delay
                            setTimeout(() => {
                                const quillTables = editor?.root?.querySelectorAll('table');
                            }, 100);
                            
                        } catch (error) {
                            console.error("Error with clipboard paste:", error);
                            // Fallback to regular setContent
                            setContent(parsedContent);
                        }
                    }
                }, 100);
            } else {
                // No tables, use regular setContent
                setContent(parsedContent);
            }
            
        } catch (error) {
            console.error("Error parsing HTML file:", error);
            alert("Error reading the HTML file. Please try again.");
        }
    };

    // Handle escape key for fullscreen
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === "Escape" && isFullscreen) {
                setIsFullscreen(false);
            }
        };

        document.addEventListener("keydown", handleEscKey);
        return () => {
            document.removeEventListener("keydown", handleEscKey);
        };
    }, [isFullscreen]);

    // Add undo/redo button icons
    useEffect(() => {
        if (quillRef.current) {
            const toolbar = quillRef.current.getEditor().getModule('toolbar');
            
            const undoButton = toolbar.container.querySelector('.ql-undo');
            const redoButton = toolbar.container.querySelector('.ql-redo');
            
            if (undoButton) {
                undoButton.innerHTML = '<i class="fas fa-undo"></i>';
                undoButton.title = 'Undo';
            }
            
            if (redoButton) {
                redoButton.innerHTML = '<i class="fas fa-redo"></i>';
                redoButton.title = 'Redo';
            }
        }
    }, [quillRef.current]);

    const modules = useMemo(
        () => ({
            toolbar: {
                container: [
                    [FONT_TOOLBAR_ENTRY],
                    [{ header: 1 }, { header: 2 }, { header: 3 }, { header: 4 }],
                    [{ size: ["small", false, "large"] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ color: [] }, { background: [] }],
                    [{ script: "sub" }, { script: "super" }],
                    [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
                    [{ indent: "-1" }, { indent: "+1" }],
                    [{ align: [] }],
                    ["blockquote", "code-block"],
                    ["link", "image", "video"],
                    ["inline-code"],
                    ["formula"],
                    ["table-better"],
                    ["clean"],
                    ["undo", "redo"],
                ],
                handlers: {
                    undo: function() {
                        this.quill.history.undo();
                    },
                    redo: function() {
                        this.quill.history.redo();
                    }
                }
            },
            history: {
                delay: 1000,
                maxStack: 100,
                userOnly: true
            },
            imageResize: {
                parchment: Quill.import("parchment"),
                modules: ["Resize", "DisplaySize"],
            },
            table: false,
            "table-better": {
                language: "en_US",
                menus: [
                    "column",
                    "row",
                    "merge",
                    "table",
                    "cell",
                    "wrap",
                    "copy",
                    "delete",
                ],
                toolbarTable: true,
                operationMenu: {
                    items: {
                        insertColumnLeft: {
                            text: 'Insert column left'
                        },
                        insertColumnRight: {
                            text: 'Insert column right'  
                        },
                        insertRowUp: {
                            text: 'Insert row above'
                        },
                        insertRowDown: {
                            text: 'Insert row below'
                        },
                        mergeCells: {
                            text: 'Merge cells'
                        },
                        unmergeCells: {
                            text: 'Unmerge cells'
                        },
                        deleteColumn: {
                            text: 'Delete column'
                        },
                        deleteRow: {
                            text: 'Delete row'
                        },
                        deleteTable: {
                            text: 'Delete table'
                        }
                    }
                }
            },
            keyboard: {
                bindings: QuillTableBetter.keyboardBindings,
            },
        }),
        []
    );

    const handleBackClick = () => {
        if (onBack) {
            setIsTransitioning(true);
            setTimeout(() => {
                onBack();
            }, 150);
        }
    };

    const handleNextClick = () => {
        // Validate that content exists
        if (!content || content.trim() === "") {
            alert("Content cannot be empty. Please ensure your document was imported correctly.");
            return;
        }

        setIsTransitioning(true);
        setTimeout(() => {
            setShowFinalize(true);
        }, 150);
    };

    const handleBackFromFinalize = () => {
        setShowFinalize(false);
        setIsTransitioning(false);
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    if (showFinalize) {
        return (
            <FinalizeImportedWorkspaceDocument 
                onBack={handleBackFromFinalize}
                documentTitle={documentTitle}
                selectedFile={selectedFile}
                imageAttachments={imageAttachments}
                content={content}
                workspaceId={workspaceId}
                enhancedRightClickEnabled={enhancedRightClickEnabled}
                showContextMenu={showContextMenu}
                ContextMenuComponent={ContextMenuComponent}
            />
        );
    }

    return (
        <div
            className={`ImportDocument__main-div ${isTransitioning ? 'transitioning-back' : ''}`}
            onContextMenu={
                enhancedRightClickEnabled
                    ? (e) => {
                        e.preventDefault();
                        showContextMenu(e.clientX, e.clientY);
                    }
                    : undefined
            }
        >
            <div className="ImportDocument__content">
                <h2>Let's Review and Edit Your Workspace Document...</h2>
                
                <p className="medium" style={{ marginTop: "20px" }}>
                    Here's the content from your imported document. You can edit it as needed before finalizing 
                    and adding it to your workspace.
                </p>

                <div style={{ marginTop: "20px", marginBottom: "20px" }}>
                    <p><strong>Document Title:</strong> {documentTitle}</p>
                    <p><strong>File:</strong> {selectedFile?.name}</p>
                    <p><strong>Image Attachments:</strong> {imageAttachments.length} files</p>
                </div>

                {/* Quill Editor */}
                <div className="ViewAndEditUploadedFile__editor-container DocEditor DocEditor--ws">
                    <div
                        className={`ViewAndEditUploadedFile__quill-container ${
                            isFullscreen ? "fullscreen-mode" : ""
                        }`}
                    >
                        <ReactQuill
                            ref={quillRef}
                            value={content}
                            onChange={setContent}
                            modules={modules}
                            style={{ height: isFullscreen ? "100vh" : "60vh" }}
                        />
                        <TaskReferenceDropdown {...taskRefProps} />
                        <UserMentionDropdown {...userMentionProps} />
                        <button
                            onClick={toggleFullscreen}
                            className="ViewAndEditUploadedFile__fullscreen-btn"
                            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                        >
                            <i
                                className={`fas ${
                                    isFullscreen ? "fa-compress-alt" : "fa-expand-alt"
                                }`}
                            ></i>
                        </button>
                    </div>
                </div>
            </div>

            {/* Back Arrow */}
            <div 
                className="ImportDocument__arrow ImportDocument__arrow--back" 
                onClick={handleBackClick}
                title="Go back"
                aria-label="Go back to previous step"
            >
                ❮
            </div>

            {/* Forward Arrow */}
            <div 
                className="ImportDocument__arrow ImportDocument__arrow--forward" 
                onClick={handleNextClick}
                title="Finalize import"
                aria-label="Finalize document import"
            >
                ❯
            </div>
            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );
}

export default ViewAndEditUploadedWorkspaceFile;
